/**
 * The screen before the app.
 *
 * Two jobs. On a cold start it covers the moment between the window appearing
 * and the first paint of anything real, so the app never shows a half-built
 * frame. When the server cannot be reached it is the whole app: there is
 * nothing truthful to draw without one, and a sign-in form over a dead
 * connection is worse than an honest wait.
 */

export function BootScreen({ note, offline }: {
  /** A line under the bar, when there is something to say. */
  note?: string;
  /** Waiting on a server that is not answering, rather than on a first load. */
  offline?: boolean;
}) {
  return (
    <div className="grid h-full place-items-center bg-surface-0">
      <div className="flex flex-col items-center gap-4 px-8">
        <img
          src="./logo.png"
          alt="Pugo"
          width={120}
          height={120}
          className="animate-[float_2.6s_ease-in-out_infinite] drop-shadow-[0_18px_40px_rgba(0,0,0,.55)]"
        />
        <div className="text-[26px] font-extrabold tracking-[.26em] text-ink">PUGO</div>

        <div className="h-[3px] w-[190px] overflow-hidden rounded bg-surface-3">
          <i
            className="block h-full w-2/5 animate-[sweep_1.25s_ease-in-out_infinite] rounded bg-gradient-to-r from-transparent to-transparent"
            // Amber while the server is missing: the same bar, but it is
            // waiting on something rather than making progress.
            style={{ backgroundImage: `linear-gradient(90deg, transparent, ${offline ? 'var(--warn)' : 'var(--accent-hi)'}, transparent)` }}
          />
        </div>

        {note ? (
          <p className="max-w-[280px] text-center text-[12.5px] leading-relaxed text-mute">{note}</p>
        ) : null}
      </div>

      <style>{`
        @keyframes float { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-9px) } }
        @keyframes sweep { 0% { transform: translateX(-120%) } 100% { transform: translateX(320%) } }
      `}</style>
    </div>
  );
}
